import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "../components/ui/toast";

// Get all package rules — the page filters client-side
export const useGetPackageRulesQuery = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_PACKAGE_RULES],
    queryFn: () => api.get(API_ROUTES.GET_PACKAGE_RULES),
  });

  return {
    data: data?.data?.data ?? [],
    isPending,
    isError,
    error,
  };
};

// Get a single package rule
export const useGetPackageRuleByIdQuery = (id) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_PACKAGE_RULE_BY_ID, id],
    queryFn: () => api.get(`${API_ROUTES.GET_PACKAGE_RULE_BY_ID}/${id}`),
    enabled: !!id,
  });

  return {
    data: data?.data?.data ?? null,
    isPending,
    isError,
    error,
  };
};

// Bundles available to pick in the rule form
export const useGetPackageOptionsQuery = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_PACKAGE_RULE_PACKAGES],
    queryFn: () => api.get(API_ROUTES.GET_PACKAGE_RULE_PACKAGES),
  });

  return {
    data: data?.data?.data ?? [],
    isPending,
    isError,
    error,
  };
};

// The property-type and finish vocabularies a rule may match on.
// Returns { propertyTypes: [], finishes: [] } — a fixed vocabulary, so it
// never goes stale.
export const useGetMatchOptionsQuery = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_PACKAGE_RULE_MATCH_OPTIONS],
    queryFn: () => api.get(API_ROUTES.GET_PACKAGE_RULE_MATCH_OPTIONS),
    staleTime: Infinity,
  });

  return {
    data: data?.data?.data ?? { propertyTypes: [], finishes: [] },
    isPending,
    isError,
    error,
  };
};

// Offices belonging to one organization, so a rule can't be scoped to another
// organization's office
export const useGetOrganizationOfficesQuery = (organizationId) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_PACKAGE_RULE_OFFICES, organizationId],
    queryFn: () =>
      api.get(API_ROUTES.GET_PACKAGE_RULE_OFFICES, {
        params: { organizationId },
      }),
    enabled: !!organizationId,
  });

  return {
    data: data?.data?.data ?? [],
    isPending,
    isError,
    error,
  };
};

// Create a package rule
export const useCreatePackageRuleMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (payload) =>
        api.post(API_ROUTES.CREATE_PACKAGE_RULE, payload),
      onSuccess: () => {
        showToast("success", "Package rule created successfully");
      },
      onError: (err) => {
        showToast(
          "error",
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Failed to create package rule"
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries([API_ROUTES.GET_PACKAGE_RULES]);
      },
    });

  return { mutate, mutateAsync, isError, error, isLoading, isSuccess, isPending };
};

// Update a package rule
export const useUpdatePackageRuleMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: ({ _id, ...payload }) =>
        api.put(`${API_ROUTES.UPDATE_PACKAGE_RULE}/${_id}`, payload),
      onSuccess: () => {
        showToast("success", "Package rule updated successfully");
      },
      onError: (err) => {
        showToast(
          "error",
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Failed to update package rule"
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries([API_ROUTES.GET_PACKAGE_RULES]);
      },
    });

  return { mutate, mutateAsync, isError, error, isLoading, isSuccess, isPending };
};

// Delete a package rule (soft delete)
export const useDeletePackageRuleMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (id) =>
        api.delete(`${API_ROUTES.DELETE_PACKAGE_RULE}/${id}`),
      onSuccess: () => {
        showToast("success", "Package rule deleted successfully");
      },
      onError: (err) => {
        showToast(
          "error",
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Failed to delete package rule"
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries([API_ROUTES.GET_PACKAGE_RULES]);
      },
    });

  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

// Restore a soft-deleted package rule
export const useRestorePackageRuleMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (id) =>
        api.put(`${API_ROUTES.RESTORE_PACKAGE_RULE}/${id}/restore`),
      onSuccess: () => {
        showToast("success", "Package rule restored successfully");
      },
      onError: (err) => {
        showToast(
          "error",
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Failed to restore package rule"
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries([API_ROUTES.GET_PACKAGE_RULES]);
      },
    });

  return { mutate, isError, error, isLoading, isSuccess, isPending };
};
